package com.evoapps.evoshift.domain.model

import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toInstant
import kotlinx.serialization.Serializable

/**
 * SH-134: what may be wrong with a shift's clock times. Flags are never a refusal: the time is
 * recorded and the shift lands on the managers' "to review" list. Stored in `actual.flags[]`.
 * The time flags follow from the times and the workplace's [ClockReviewSettings] (functions/
 * clockReview.js computes them on every clock and after any change of the times, like
 * [ClockReviewRules.timeFlags]); the pattern flags come from the clock event itself and stay.
 */
enum class ClockFlag {
    /** Clocked in more than [ClockReviewSettings.earlyInMinutes] before the planned start. */
    EARLY_IN,
    /** Clocked out more than [ClockReviewSettings.lateOutMinutes] after the planned end. */
    LATE_OUT,
    /** Clocked longer than planned by more than [ClockReviewSettings.maxOverrunMinutes]. */
    OVERRUN,
    /** Clocked in and out within [ClockReviewSettings.veryShortMinutes]. */
    VERY_SHORT,
    /** SH-106 warn mode: clocked outside the workplace's radius. */
    OUTSIDE_AREA,
    /** Pattern: two different people clocked on the same tablet within [ClockReviewSettings.sameTabletSeconds]. */
    SAME_TABLET,
    /** Pattern: clocked on the tablet with the PIN, and the person's app was not used since the clock-in. */
    NO_APP_ACTIVITY,
    /**
     * SH-180: the person's own phone wrote a clock time (no geofence, no tablet) more than 15
     * minutes from the server's time of the write: a clock set by hand, or an offline clock-in
     * sent late. The server time and the difference are on the stamp ([ClockStamp.driftMinutes]).
     */
    CLOCK_DRIFT;

    /** Pattern flags come from the clock event and are kept when the times change. */
    val isPattern: Boolean get() = this == SAME_TABLET || this == NO_APP_ACTIVITY || this == CLOCK_DRIFT

    companion object {
        fun fromName(name: String?): ClockFlag? = entries.firstOrNull { it.name == name }
    }
}

/**
 * SH-134: `workplaces/{id}.settings.clockReview`, the thresholds of the flags and the safeguards
 * against clocking for someone else. The defaults flag clearly unusual times and change nothing
 * else: pay counts the clocked time, the tablet takes PINs, no photos.
 */
@Serializable
data class ClockReviewSettings(
    val earlyInMinutes: Int = DEFAULT_EARLY_IN_MINUTES,
    val lateOutMinutes: Int = DEFAULT_LATE_OUT_MINUTES,
    val maxOverrunMinutes: Int = DEFAULT_MAX_OVERRUN_MINUTES,
    val veryShortMinutes: Int = DEFAULT_VERY_SHORT_MINUTES,
    /**
     * "Licz czas od planowanego startu": payable time (pay estimate, labour cost, exports, tips)
     * starts no earlier than the planned start and ends no later than the planned end, unless a
     * manager approved the clocked times ([ClockReviewRules.payableTimes]).
     */
    val countFromPlannedStart: Boolean = false,
    /** "Tylko kod QR na tablecie": the tablet takes no PINs, only the person's own phone (QR, SH-111). */
    val kioskQrOnly: Boolean = false,
    /**
     * An optional photo at the tablet's clock-in (off by default). Stored privately for the
     * managers for 30 days, never used for face matching. Needs a DPIA and a staff notice first.
     */
    val kioskPhoto: Boolean = false,
    /** Two different people on the same tablet within this many seconds are flagged; 0 = off. */
    val sameTabletSeconds: Int = DEFAULT_SAME_TABLET_SECONDS,
    /** Flag a tablet PIN clock-out when the person's app was not used since the clock-in. */
    val flagNoAppActivity: Boolean = false
) {
    companion object {
        const val DEFAULT_EARLY_IN_MINUTES = 15
        const val DEFAULT_LATE_OUT_MINUTES = 15
        const val DEFAULT_MAX_OVERRUN_MINUTES = 30
        const val DEFAULT_VERY_SHORT_MINUTES = 15
        const val DEFAULT_SAME_TABLET_SECONDS = 60
        const val MAX_MINUTES = 240
        const val MAX_SECONDS = 600
        /** Photos at the tablet are deleted after this many days (storage.lifecycle.json). */
        const val PHOTO_KEEP_DAYS = 30

        /** The choices offered in the workplace settings; 0 = that flag is off. */
        val EARLY_IN_CHOICES = listOf(0, 5, 10, 15, 20, 30, 45, 60)
        val LATE_OUT_CHOICES = listOf(0, 5, 10, 15, 20, 30, 45, 60)
        val OVERRUN_CHOICES = listOf(0, 15, 30, 45, 60, 90, 120)
        val VERY_SHORT_CHOICES = listOf(0, 5, 10, 15, 30, 60)
        val SAME_TABLET_CHOICES = listOf(0, 30, 60, 120, 300)

        /** Tolerant read of stored values: out of range is the nearest allowed, negative is off. */
        fun of(
            earlyInMinutes: Int?, lateOutMinutes: Int?, maxOverrunMinutes: Int?, veryShortMinutes: Int?,
            countFromPlannedStart: Boolean?, kioskQrOnly: Boolean?, kioskPhoto: Boolean?,
            sameTabletSeconds: Int?, flagNoAppActivity: Boolean?
        ) = ClockReviewSettings(
            earlyInMinutes = (earlyInMinutes ?: DEFAULT_EARLY_IN_MINUTES).coerceIn(0, MAX_MINUTES),
            lateOutMinutes = (lateOutMinutes ?: DEFAULT_LATE_OUT_MINUTES).coerceIn(0, MAX_MINUTES),
            maxOverrunMinutes = (maxOverrunMinutes ?: DEFAULT_MAX_OVERRUN_MINUTES).coerceIn(0, MAX_MINUTES),
            veryShortMinutes = (veryShortMinutes ?: DEFAULT_VERY_SHORT_MINUTES).coerceIn(0, MAX_MINUTES),
            countFromPlannedStart = countFromPlannedStart ?: false,
            kioskQrOnly = kioskQrOnly ?: false,
            kioskPhoto = kioskPhoto ?: false,
            sameTabletSeconds = (sameTabletSeconds ?: DEFAULT_SAME_TABLET_SECONDS).coerceIn(0, MAX_SECONDS),
            flagNoAppActivity = flagNoAppActivity ?: false
        )
    }
}

/** SH-134: what a manager decided about flagged clock times. */
enum class ClockDecision {
    /** The clocked times stand and are paid as clocked. */
    APPROVED,
    /** The times were set to the planned ones. */
    ADJUSTED_TO_PLAN,
    /** The manager typed the times (they are paid as typed). */
    EDITED;

    companion object {
        fun fromName(name: String?): ClockDecision? = entries.firstOrNull { it.name == name }
    }
}

/**
 * SH-134: a manager's decision on the clock times (`actual.review`): the flags it covered, the
 * times it was made for (a later clock-out is a new question), and who made it when. Every
 * decision is also logged in `workplaces/{id}/clock_reviews` (functions/clockReview.js).
 */
data class ClockReviewDecision(
    val decision: ClockDecision,
    val flags: List<ClockFlag>,
    val start: String,
    val end: String,
    val decidedBy: String,
    val decidedByName: String?,
    val decidedAt: String
)

/** SH-134: someone clocked in without a shift of their own. */
enum class UnplannedKind {
    /** On a colleague's shift at that time ("Anna instead of Kasia"). */
    COVER,
    /** Nobody had a shift then: a new shift for the person. */
    EXTRA;

    companion object {
        fun fromName(name: String?): UnplannedKind? = entries.firstOrNull { it.name == name }
    }
}

enum class UnplannedStatus {
    PENDING, CONFIRMED, REJECTED;

    companion object {
        fun fromName(name: String?): UnplannedStatus? = entries.firstOrNull { it.name == name }
    }
}

/**
 * SH-134: an unplanned clock-in (`shifts/{id}.unplanned`), written only by the clock functions.
 * [COVER][UnplannedKind.COVER]: [userId] clocked in on [coveredUserId]'s shift; until a manager
 * confirms, the shift stays the planned person's, and confirming records [userId] as who worked
 * it (`actualUserId`, SH-128), so the cost moves to them. [EXTRA][UnplannedKind.EXTRA]: a new shift
 * for [userId]; confirming keeps it with the clocked times as planned, rejecting removes it.
 */
data class UnplannedClock(
    val kind: UnplannedKind,
    val userId: String,
    val userName: String?,
    val coveredUserId: String?,
    val coveredUserName: String?,
    val status: UnplannedStatus,
    val via: ClockSource?,
    val at: String,
    val decidedBy: String? = null,
    val decidedAt: String? = null,
    val decidedByName: String? = null
) {
    val isPending: Boolean get() = status == UnplannedStatus.PENDING
}

/** SH-134: the review side of a shift's clock: flags, the manager's decision, the tablet photos. */
data class ClockCheck(
    val flags: List<ClockFlag> = emptyList(),
    val review: ClockReviewDecision? = null,
    /** Storage paths of the tablet's clock-in / clock-out photos (managers only, 30 days). */
    val startPhotoPath: String? = null,
    val endPhotoPath: String? = null
)

/** What a manager can do with an item on the "to review" list. */
enum class ClockReviewAction {
    APPROVE, ADJUST_TO_PLAN, EDIT_TIMES, CONFIRM_UNPLANNED, REJECT_UNPLANNED
}

/**
 * SH-134: the flag and payable-time rules. Mirrored by functions/clockReview.js (flags on the
 * server, payable time in the exports and the tip split); the tests of both check the same cases.
 */
object ClockReviewRules {
    /**
     * The flags that follow from the times: [plannedStart]..[plannedEnd] against the clocked
     * [actualStart]..[actualEnd] (local ISO; the end empty while working). A plan without length
     * (an unplanned extra shift) has nothing to compare with: only [ClockFlag.VERY_SHORT].
     */
    fun timeFlags(
        plannedStart: String, plannedEnd: String, actualStart: String, actualEnd: String,
        settings: ClockReviewSettings
    ): List<ClockFlag> {
        val ps = parse(plannedStart)
        val pe = parse(plannedEnd)
        val ast = parse(actualStart) ?: return emptyList()
        val ae = parse(actualEnd)
        val planned = ps != null && pe != null && pe > ps
        val flags = mutableListOf<ClockFlag>()
        if (planned && settings.earlyInMinutes > 0 && minutes(ast, ps!!) > settings.earlyInMinutes) flags += ClockFlag.EARLY_IN
        if (ae != null && ae > ast) {
            if (planned && settings.lateOutMinutes > 0 && minutes(pe!!, ae) > settings.lateOutMinutes) flags += ClockFlag.LATE_OUT
            if (planned && settings.maxOverrunMinutes > 0 &&
                minutes(ast, ae) - minutes(ps!!, pe!!) > settings.maxOverrunMinutes
            ) flags += ClockFlag.OVERRUN
            if (settings.veryShortMinutes > 0 && minutes(ast, ae) < settings.veryShortMinutes) flags += ClockFlag.VERY_SHORT
        }
        return flags
    }

    /**
     * Whether a manager should look at the shift: an unplanned clock-in waiting for a decision, or
     * a flag the last decision did not cover (a clock-out after an approved early clock-in asks again).
     */
    fun needsReview(shift: Shift): Boolean {
        if (shift.unplanned?.isPending == true) return true
        val flags = shift.clockCheck?.flags.orEmpty()
        if (flags.isEmpty()) return false
        val review = shift.clockCheck?.review ?: return true
        return flags.any { it !in review.flags }
    }

    /**
     * The clocked times that count for pay (clocked in and out; null otherwise). With
     * [ClockReviewSettings.countFromPlannedStart] the start is no earlier than the planned start
     * and the end no later than the planned end, unless a manager approved (or typed) exactly
     * these times. A late start or an early end counts as clocked. An unplanned extra shift
     * (a plan without length) counts nothing until it is confirmed. Mirrors clockReview.js.
     */
    fun payableTimes(shift: Shift, settings: ClockReviewSettings): TimeRange? {
        val actual = shift.actualTime?.takeIf { it.start.isNotEmpty() && it.end.isNotEmpty() } ?: return null
        if (!settings.countFromPlannedStart) return actual
        // A confirmed extra shift was decided as clocked (its plan may still have no length).
        if (shift.unplanned?.kind == UnplannedKind.EXTRA && shift.unplanned.status == UnplannedStatus.CONFIRMED) return actual
        val review = shift.clockCheck?.review?.takeIf { it.decision != ClockDecision.ADJUSTED_TO_PLAN }
        val startApproved = review != null && review.start == actual.start
        val endApproved = review != null && review.end == actual.end
        val ps = shift.plannedTime.start
        val pe = shift.plannedTime.end
        val start = if (!startApproved && ps.isNotEmpty() && sameMinuteOrder(actual.start, ps) < 0) ps else actual.start
        val end = if (!endApproved && pe.isNotEmpty() && sameMinuteOrder(actual.end, pe) > 0) pe else actual.end
        if (sameMinuteOrder(end, start) <= 0) return TimeRange(start, start)
        return TimeRange(start, end)
    }

    /** Minutes the clocked times are longer than the payable ones (what an approval would add). */
    fun unpaidExtraMinutes(shift: Shift, settings: ClockReviewSettings): Long {
        val actual = shift.actualTime?.takeIf { it.start.isNotEmpty() && it.end.isNotEmpty() } ?: return 0
        val payable = payableTimes(shift, settings) ?: return 0
        val a = parse(actual.start) ?: return 0
        val b = parse(actual.end) ?: return 0
        val c = parse(payable.start) ?: return 0
        val d = parse(payable.end) ?: return 0
        return (minutes(a, b) - minutes(c, d)).coerceAtLeast(0)
    }

    private fun sameMinuteOrder(a: String, b: String): Int {
        val x = parse(a)
        val y = parse(b)
        return if (x != null && y != null) x.compareTo(y) else a.take(16).compareTo(b.take(16))
    }

    private fun parse(value: String): LocalDateTime? = BreakRules.parse(value)

    private fun minutes(from: LocalDateTime, to: LocalDateTime): Long =
        (to.toInstant(TimeZone.UTC) - from.toInstant(TimeZone.UTC)).inWholeMinutes
}

/** SH-134: the clocked times that count for pay under [settings] (see [ClockReviewRules.payableTimes]). */
fun Shift.payableTimes(settings: ClockReviewSettings): TimeRange? = ClockReviewRules.payableTimes(this, settings)

/** SH-134: whether the shift is on the managers' "to review" list. */
val Shift.needsClockReview: Boolean get() = ClockReviewRules.needsReview(this)

/** SH-134: why reviewClock refused a decision (the screen words it). */
class ClockReviewException(val reason: Reason) : Exception("Clock review: $reason") {
    enum class Reason {
        /** The unplanned clock-in was decided already. */
        NOTHING_TO_DECIDE,
        /** Nobody clocked in on the shift. */
        NOT_CLOCKED_IN,
        /** The shift has no planned times to adjust to. */
        NO_PLAN,
        /** The end is not after the start. */
        BAD_TIMES,
        DAY_CLOSED,
        PLAN_ENDED,
        /** SH-257: a manager recorded that nobody worked the shift. */
        NOT_WORKED
    }
}
